import { useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { importFiles, addPhotosToDocument } from "../services/naraDoor";
import { getCurrentWorkspaceId } from "../lib/workspaceStore";

export type ImportMode = "bundle" | "multi-document";

export interface FileUploadProgress {
  filename: string;
  progress: number; // 0-100
  status: "pending" | "uploading" | "done" | "error";
  error?: string;
}

export interface UseFileUploadOptions {
  projectId: string;
  onComplete?: () => void;
}

export function useFileUpload({ projectId, onComplete }: UseFileUploadOptions) {
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [fileProgress, setFileProgress] = useState<FileUploadProgress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const [documentsCreated, setDocumentsCreated] = useState(0);
  const [createdDocumentIds, setCreatedDocumentIds] = useState<string[]>([]);
  const abortControllersRef = useRef<XMLHttpRequest[]>([]);

  const uploadFileToGcs = useCallback((file: File, signedUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      abortControllersRef.current.push(xhr);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const pct = Math.round((event.loaded / event.total) * 100);
          setFileProgress(prev => prev.map(fp =>
            fp.filename === file.name ? { ...fp, progress: pct, status: "uploading" } : fp
          ));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setFileProgress(prev => prev.map(fp =>
            fp.filename === file.name ? { ...fp, progress: 100, status: "done" } : fp
          ));
          resolve();
        } else {
          setFileProgress(prev => prev.map(fp =>
            fp.filename === file.name ? { ...fp, status: "error", error: `HTTP ${xhr.status}` } : fp
          ));
          reject(new Error(`Upload failed: HTTP ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        setFileProgress(prev => prev.map(fp =>
          fp.filename === file.name ? { ...fp, status: "error", error: "Network error" } : fp
        ));
        reject(new Error("Network error"));
      };

      xhr.open("PUT", signedUrl);
      xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
      xhr.send(file);
    });
  }, []);

  const startUpload = useCallback(async (files: File[], mode: ImportMode, documentTitle?: string) => {
    setIsUploading(true);
    setError(null);
    setIsDone(false);
    setDocumentsCreated(0);
    setCreatedDocumentIds([]);
    setFileProgress(files.map(f => ({ filename: f.name, progress: 0, status: "pending" })));

    // callApp sends the current workspace as the header.
    if (!getCurrentWorkspaceId()) {
      setError("No workspace selected");
      setIsUploading(false);
      return;
    }

    try {
      if (mode === "bundle") {
        // One document with all files
        const title = documentTitle || files[0].name.replace(/\.[^/.]+$/, "");
        const result = await importFiles(projectId, {
          documentTitle: title,
          files: files.map(f => ({
            filename: f.name,
            content_type: f.type || "application/octet-stream",
            size_bytes: f.size,
          })),
        });

        // Invalidate documents list so new doc appears
        queryClient.invalidateQueries({ queryKey: ["documents", projectId] });

        // Upload each file to its signed URL (index-based: uploads array matches input files order)
        const uploadPromises = result.uploads.map((upload, i) => {
          return uploadFileToGcs(files[i], upload.signed_url);
        });
        await Promise.all(uploadPromises);
        setDocumentsCreated(1);
        setCreatedDocumentIds([result.document_id]);
      } else {
        // One document per file
        let created = 0;
        const ids: string[] = [];
        const allUploadPromises: Promise<void>[] = [];

        for (const file of files) {
          const title = file.name.replace(/\.[^/.]+$/, "");
          const result = await importFiles(projectId, {
            documentTitle: title,
            files: [{
              filename: file.name,
              content_type: file.type || "application/octet-stream",
              size_bytes: file.size,
            }],
          });
          created++;
          ids.push(result.document_id);

          // Invalidate after each document creation so they appear in the list
          queryClient.invalidateQueries({ queryKey: ["documents", projectId] });

          const upload = result.uploads[0];
          allUploadPromises.push(uploadFileToGcs(file, upload.signed_url));
        }

        await Promise.all(allUploadPromises);
        setDocumentsCreated(created);
        setCreatedDocumentIds(ids);
      }

      setIsDone(true);
      onComplete?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setIsUploading(false);
      abortControllersRef.current = [];
    }
  }, [projectId, uploadFileToGcs, queryClient, onComplete]);

  /**
   * Upload photos to an existing document (adds to its image_files).
   * Uses addPhotosToDocument instead of importFiles, then uploads to GCS.
   * Invalidates documentView query on completion so new photos appear.
   */
  const uploadToDocument = useCallback(async (files: File[], documentId: string) => {
    setIsUploading(true);
    setError(null);
    setIsDone(false);
    setDocumentsCreated(0);
    setFileProgress(files.map(f => ({ filename: f.name, progress: 0, status: "pending" })));

    // callApp sends the current workspace as the header.
    if (!getCurrentWorkspaceId()) {
      setError("No workspace selected");
      setIsUploading(false);
      return;
    }

    try {
      const result = await addPhotosToDocument(projectId, documentId, files.map(f => ({
        filename: f.name,
        content_type: f.type || "application/octet-stream",
        size_bytes: f.size,
      })));

      // Upload each file to its signed URL
      const uploadPromises = result.uploads.map((upload, i) => {
        return uploadFileToGcs(files[i], upload.signed_url);
      });
      await Promise.all(uploadPromises);

      // Invalidate both document view and documents list
      queryClient.invalidateQueries({ queryKey: ["documentView", documentId] });
      queryClient.invalidateQueries({ queryKey: ["documents", projectId] });

      setIsDone(true);
      onComplete?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setIsUploading(false);
      abortControllersRef.current = [];
    }
  }, [projectId, uploadFileToGcs, queryClient, onComplete]);

  const reset = useCallback(() => {
    setIsUploading(false);
    setFileProgress([]);
    setError(null);
    setIsDone(false);
    setDocumentsCreated(0);
    setCreatedDocumentIds([]);
  }, []);

  return {
    startUpload,
    uploadToDocument,
    isUploading,
    fileProgress,
    error,
    isDone,
    documentsCreated,
    createdDocumentIds,
    reset,
  };
}
